// Models
import { TWeekdays } from 'models'
import { IconsType as StylizedIconsType } from 'heeds-ds/src/components/UI/Icons/StylizedIcons/values'
import { IAppointmentTime } from 'storage/profile/models'
import { IFormFields } from 'views/dashboard/athlete/AthleteSchedule'

// Libraries
import moment from 'moment'

export enum EDaysName {
  sun = 'Domingo',
  mon = 'Segunda',
  tue = 'Terça',
  wed = 'Quarta',
  thu = 'Quinta',
  fri = 'Sexta',
  sat = 'Sábado',
}

export const daysName = {
  sun: 'Domingo',
  mon: 'Segunda',
  tue: 'Terça',
  wed: 'Quarta',
  thu: 'Quinta',
  fri: 'Sexta',
  sat: 'Sábado',
}

export const isWebviewAccess = () => {
  const webview = localStorage.getItem('webview')
  return !!webview
}

export const convertOptionToFilter = (
  options: Array<{ label: string; value: string }>,
  param: string,
) => {
  if (!options) return []

  return options.map((option) => ({ ...option, param }))
}

export const dateFormatEnglishToPortuguese = (date?: string) => {
  if (!date) return ''
  return moment(date, 'YYYY-MM-DD').format('DD/MM/YYYY')
}

export const dateFormatPortugueseToEnglish = (date?: string) => {
  if (typeof date === 'string') {
    if (date?.includes('-')) {
      return date
    }
  }
  return moment(date, 'DD/MM/YYYY').format('YYYY-MM-DD')
}

export function firstLetter(name: string) {
  return name.trim().charAt(0).toUpperCase()
}

export function getDateFromPastYears(yearsAgo: number): Date {
  const pastTimeStamp = new Date().setFullYear(
    new Date().getFullYear() - yearsAgo,
  )
  return new Date(pastTimeStamp)
}

export function getAlphabeticLetter(position: number) {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz'.split('')
  return alphabet[position % 26]
}

export function getWeekDay(position: number) {
  const weekDays = [
    'Domingo',
    'Segunda',
    'Terça',
    'Quarta',
    'Quinta',
    'Sexta',
    'Sábado',
  ]
  return weekDays[position % 7]
}

export const getDivisionType = (modelName: string) => {
  const numericRegex = /^\d+$/
  const alphabetRegex = /^\w$/
  const lastName = modelName
    .trim()
    .split(' ')
    .reduce((_, text) => text)

  if (numericRegex.test(lastName)) return 'NU'

  if (alphabetRegex.test(lastName)) return 'AP'

  return 'DW'
}

export const getModelName = (index: number, division: string | null) => {
  switch (division) {
    case 'NU':
      return `Treino ${index + 1}`
    case 'DW':
      return `Treino de ${getWeekDay(index)}`
    default:
      return `Treino ${getAlphabeticLetter(index)}`
  }
}

export function getGoalIcon(goal?: string): StylizedIconsType {
  switch (goal) {
    case 'Hipertrofia':
      return 'hypertrophy'
    case 'Redução de Gordura':
      return 'fatLoss'
    case 'Definição Muscular':
      return 'definition'
    case 'Prevenção de Lesões':
      return 'injuryPrevention'
    case 'Condicionamento Físico':
      return 'conditioning'
    case 'Qualidade de Vida':
      return 'lifeQuality'
    case 'Treinamento Funcional':
      return 'functionalTraining'
    default:
      return 'genericWorkout'
  }
}

export function mergeCref(cref = '', cref_UF = '') {
  return `${cref}/${cref_UF}`
}

export function splitCref(cref: string) {
  const crefSplitted = cref.split('/')
  const crefNumbers = crefSplitted[0]
  const crefUF = crefSplitted[1]
  return [crefNumbers, crefUF]
}

export function renderInitials(name = '') {
  const { first_name, last_name } = splitFullname(name)

  return (
    first_name?.charAt(0).toUpperCase() + last_name?.charAt(0).toUpperCase()
  )
}

export function renderDaysInFrequency(days?: TWeekdays[]) {
  if (!days) return

  if (days.length === 7) {
    return 'Todos os dias'
  } else if (
    days.length === 5 &&
    !days.includes('sat') &&
    !days.includes('sun')
  ) {
    return 'Dias de semana'
  } else if (
    days.length === 2 &&
    days.includes('sat') &&
    days.includes('sun')
  ) {
    return 'Finais de semana'
  } else {
    const selectedDays = Object.keys(EDaysName).filter((day) =>
      days.includes(day as TWeekdays),
    )
    return selectedDays
      .map((selectedDay, index) => {
        const day = selectedDay as TWeekdays
        if (index === days.length - 1 && days.length > 1) {
          return ` e ${EDaysName[day]}s`
        }
        return index === 0 ? `${EDaysName[day]}s` : `, ${EDaysName[day]}s`
      })
      .join('')
  }
}

export function resizeTextArea(textAreaRef: HTMLTextAreaElement) {
  textAreaRef.style.height = '0px'
  textAreaRef.style.height = `${textAreaRef.scrollHeight}px`
}

export function sliceStringByLength(text: string, length: number) {
  if (text.length + 4 > length) {
    return text.slice(0, length) + '... '
  }
  return text.slice(0, length)
}

export function splitFullname(name = '') {
  const [first_name, ...restNames] = name.trim().split(' ')
  const last_name = restNames.length ? restNames.reverse()[0] : ''

  return { first_name, last_name }
}

export const toSnakeCase = (text?: string) =>
  text
    ?.match(
      /[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g,
    )
    ?.map((x) => x.toLowerCase())
    .join('_')

export function uid() {
  return Date.now().toString(36) + Math.random().toString(36).replace('0.', '')
}

export function genderHandler(gender: string) {
  switch (gender) {
    case 'M':
      return 'Masculino'
    case 'F':
      return 'Feminino'
  }
}

export function hourMinuteFormat(time: string) {
  const split = time?.slice(0, 5).split(':')
  const leftSide =
    split[0] === '00'
      ? '0'
      : split[0].slice(0, 1) === '0'
      ? split[0].slice(1, 2)
      : split[0]
  const rightSide = split[1] === '00' ? '' : split[1]
  return leftSide + 'h' + rightSide
}

export function generateScheduleResetObject(
  appointmentTimes: IAppointmentTime[],
) {
  const object: IFormFields = {} as IFormFields
  appointmentTimes?.forEach((appointment: IAppointmentTime) => {
    object[appointment.day] = {
      id: appointment.id,
      day: appointment.day,
      start: appointment?.start.slice(0, 5),
      end: appointment?.end.slice(0, 5),
      address: appointment?.address,
    }
  })
  return object
}

export function calculateAge(birthdate: string) {
  const date = new Date(birthdate)
  const currentDate = new Date()
  const difference = currentDate.getTime() - date.getTime()
  const ageDate = new Date(difference)
  const years = ageDate.getUTCFullYear() - 1970

  return years
}
